/**
 * 04 · The paywall.
 *
 * One long scroll on mobile, one centred canvas on desktop, same sections in
 * the same order: what changes → what you get → what is inside → what people
 * say → what it costs → the guarantee → the questions.
 *
 * It sells on understanding rather than urgency: the person has already
 * finished the test, so every block shows what has been computed and what stays
 * locked. The countdown is the promo's, not the offer's — the trial is there
 * whether or not the clock runs out.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../app/icons';
import { c, hair, hair2, f, ACT } from '../app/tokens';
import { Acc, Col, Meta, Muted, Row, Appear } from '../app/primitives';
import { Orb } from '../app/orb';
import { TFn, useTt } from '../i18n/useT';
import { PRICE_MONTH, REFUND_DAYS, TRIAL_DAYS, PromoState, money, perDay } from '../config/paywall';
import { PaywallProfile } from './data';
import { phoneShots, reviews, shotPairs, SHOT_ASPECT, Shot } from './copy';
import { Assurances, Carousel, Countdown, Cta, OK, ReviewCard, Screen, SectHead, Serif, useWide } from './ui';

/** How long a review stays up before the carousel turns itself. */
const REVIEW_MS = 4200;

/** Product shots get longer — there is more to take in than in a quote. */
const SHOT_MS = 5200;

/** Opacity of the viewport the switch is not on. Present, but clearly secondary. */
const DIMMED = 0.34;

/* ── before / after ───────────────────────────────────────────────────────── */

const Side: React.FC<{ tag: string; score: number; good?: boolean; lines: string[]; wide?: boolean }> = ({
    tag,
    score,
    good,
    lines,
    wide,
}) => {
    const { t } = useTt();

    return (
    <Col
        style={{
            flex: 1,
            minWidth: 0,
            padding: wide ? '26px 26px 24px' : '16px 13px 15px',
            borderRadius: wide ? 22 : 18,
            border: good ? `1px solid ${OK}33` : hair,
            background: good ? `${OK}0a` : c.cardIn,
            alignItems: 'center',
        }}
        gap={wide ? 18 : 12}
    >
        <Meta style={{ fontSize: wide ? 10 : 9, letterSpacing: '0.13em', color: good ? OK : c.muted }}>{tag}</Meta>
        {/* The "now" side is dimmed so the eye lands on the outcome, without
            hiding the number the person actually scored. */}
        <div style={{ opacity: good ? 1 : 0.55 }}>
            <Orb score={score} size={wide ? 200 : 116} label={t('pw.pay.orbLabel')} />
        </div>
        <Col
            style={{ width: '100%', paddingTop: wide ? 16 : 12, borderTop: good ? `1px solid ${OK}22` : hair2 }}
            gap={wide ? 12 : 8}
        >
            {lines.map((line) => (
                <Row key={line} style={{ gap: wide ? 11 : 8, alignItems: 'flex-start' }}>
                    <Icon
                        k={good ? 'check' : 'close'}
                        size={wide ? 13 : 10}
                        color={good ? OK : 'rgba(232,230,224,0.32)'}
                        style={{ marginTop: 2, flex: '0 0 auto' }}
                    />
                    <span
                        style={{
                            fontSize: wide ? 13.5 : 11,
                            color: good ? c.text : c.muted,
                            fontWeight: 300,
                            lineHeight: 1.42,
                        }}
                    >
                        {line}
                    </span>
                </Row>
            ))}
        </Col>
    </Col>
    );
};

const BeforeAfter: React.FC<{ profile: PaywallProfile; wide?: boolean }> = ({ profile, wide }) => {
    const { t } = useTt();
    const proj = profile.projection;

    if (!proj) return null;

    return (
        <div>
            <Row style={{ gap: wide ? 22 : 11, alignItems: 'stretch' }}>
                <Side
                    tag={t('pw.pay.now')}
                    score={proj.now}
                    lines={[1, 2, 3, 4].map((i) => t(`pw.pay.now.${i}`))}
                    wide={wide}
                />
                <Side
                    tag={t('pw.pay.then')}
                    score={proj.goal}
                    good
                    lines={[1, 2, 3, 4].map((i) => t(`pw.pay.then.${i}`))}
                    wide={wide}
                />
            </Row>
            <Muted style={{ fontSize: wide ? 11.5 : 10.5, marginTop: 11, textAlign: wide ? 'center' : 'left' }}>
                {t('pw.model')}
            </Muted>
        </div>
    );
};

/* ── product shots ────────────────────────────────────────────────────────── */

/** Phone frame whose height comes from the screenshot's real aspect ratio. */
const PhoneShot: React.FC<{ shot: Shot; width: number }> = ({ shot, width }) => {
    const height = Math.round(width / SHOT_ASPECT);

    return (
        <Col style={{ width }} gap={13}>
            <div
                style={{
                    width,
                    height,
                    borderRadius: 28,
                    padding: 6,
                    background: '#000',
                    border: '1px solid rgba(232,230,224,0.18)',
                    boxSizing: 'border-box',
                    boxShadow: '0 20px 46px rgba(0,0,0,0.55)',
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        height: '100%',
                        borderRadius: 23,
                        background: c.bg,
                        overflow: 'hidden',
                    }}
                >
                    {/* Below the fold on every viewport, and the source files
                        are full-resolution product captures — deferring them
                        keeps the paywall's first paint off a 2MB download. */}
                    <img
                        src={shot.src}
                        alt={shot.title}
                        loading="lazy"
                        decoding="async"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'top',
                            display: 'block',
                        }}
                    />
                    {/* The fade stops above the app's own tab bar so the menu at
                        the bottom of the screenshot stays legible. */}
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: '26%',
                            height: Math.round(height * 0.16),
                            background: `linear-gradient(180deg, transparent, ${c.bg}cc)`,
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            </div>
            <Col gap={5}>
                <span style={{ fontSize: 14, color: c.textPri, fontWeight: 400, letterSpacing: '-0.015em' }}>
                    {shot.title}
                </span>
                <Muted style={{ fontSize: 12 }}>{shot.sub}</Muted>
            </Col>
        </Col>
    );
};

const LaptopShot: React.FC<{ shot: Shot; width: number }> = ({ shot, width }) => (
    <Col style={{ width }} gap={13}>
        <div style={{ width, borderRadius: 14, overflow: 'hidden', boxShadow: '0 20px 46px rgba(0,0,0,0.5)' }}>
            <img
                src={shot.src}
                alt={shot.title}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', display: 'block' }}
            />
        </div>
        <Col gap={5}>
            <span style={{ fontSize: 14, color: c.textPri, fontWeight: 400, letterSpacing: '-0.015em' }}>
                {shot.title}
            </span>
            <Muted style={{ fontSize: 12 }}>{shot.sub}</Muted>
        </Col>
    </Col>
);

/** Desktop's two-segment switch between the phone and the laptop carousels. */
const ViewSwitch: React.FC<{ value: 'mobile' | 'desktop'; onChange: (v: 'mobile' | 'desktop') => void }> = ({
    value,
    onChange,
}) => {
    const { t } = useTt();
    const options: Array<['mobile' | 'desktop', string]> = [
        ['mobile', t('pw.pay.view.mobile')],
        ['desktop', t('pw.pay.view.desktop')],
    ];

    return (
        <Row style={{ gap: 6, padding: 3, borderRadius: 99, border: hair, background: c.card, alignSelf: 'center' }}>
            {options.map(([key, label]) => (
                <button
                    key={key}
                    type="button"
                    onClick={() => onChange(key)}
                    style={{
                        height: 34,
                        padding: '0 18px',
                        borderRadius: 99,
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: f.ui,
                        fontSize: 12.5,
                        fontWeight: value === key ? 500 : 400,
                        color: value === key ? '#0a0a0a' : c.muted,
                        background: value === key ? c.textPri : 'transparent',
                        transition: 'background 220ms, color 220ms',
                    }}
                >
                    {label}
                </button>
            ))}
        </Row>
    );
};

/* ── what is inside ───────────────────────────────────────────────────────── */

const INSIDE_ICONS = ['tDirs', 'tPats', 'tTasks', 'tIns', 'report'];

const Inside: React.FC<{ counts: { patterns: number; exercises: number }; wide?: boolean }> = ({ counts, wide }) => {
    const { t, tn } = useTt();
    const items = INSIDE_ICONS.map((ic, i) => {
        const n = i + 1;
        // Rows 2 and 3 print a count, and the noun after it has to agree with
        // that count — Ukrainian takes three forms, so "53 патерни" and
        // "67 вправ" decline differently off the same sentence.
        const counted: Record<number, number> = { 2: counts.patterns, 3: counts.exercises };
        const key = `pw.pay.inside.${n}.title`;
        const title = counted[n] === undefined ? t(key) : tn(key, counted[n]);

        return { ic, title, sub: t(`pw.pay.inside.${n}.sub`) };
    });

    return (
        <div
            style={
                wide
                    ? { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16 }
                    : { display: 'flex', flexDirection: 'column', gap: 9 }
            }
        >
            {items.map((it) => (
                <Col
                    key={it.title}
                    style={{
                        padding: wide ? '22px 24px' : '12px 13px',
                        background: c.cardIn,
                        border: hair,
                        borderRadius: wide ? 18 : 14,
                        flexDirection: wide ? 'column' : 'row',
                        alignItems: wide ? 'stretch' : 'flex-start',
                        gap: wide ? 13 : 12,
                    }}
                >
                    <div
                        style={{
                            width: wide ? 38 : 30,
                            height: wide ? 38 : 30,
                            borderRadius: wide ? 12 : 9,
                            flex: '0 0 auto',
                            display: 'grid',
                            placeItems: 'center',
                            background: `${ACT}12`,
                            border: `1px solid ${ACT}28`,
                        }}
                    >
                        <Icon k={it.ic} size={wide ? 17 : 14} color={ACT} />
                    </div>
                    <Col gap={wide ? 13 : 4}>
                        <span
                            style={{
                                fontSize: wide ? 16 : 13.5,
                                color: c.textPri,
                                fontWeight: 400,
                                letterSpacing: '-0.013em',
                            }}
                        >
                            {it.title}
                        </span>
                        <Muted style={{ fontSize: wide ? 13.5 : 11.5, lineHeight: 1.5 }}>{it.sub}</Muted>
                    </Col>
                </Col>
            ))}
        </div>
    );
};

/* ── price ladder ─────────────────────────────────────────────────────────── */

interface Step {
    tag: string;
    title: string;
    sub: string;
    was: string | null;
    price: string;
    day: string;
    main?: boolean;
}

function priceSteps(t: TFn, promo: PromoState | null): Step[] {
    const steps: Step[] = [
        {
            tag: t('pw.pay.step1.tag'),
            title: t('pw.pay.step1.title'),
            sub: t('pw.pay.step1.sub'),
            was: null,
            price: '$0',
            day: '$0.00',
            main: true,
        },
    ];

    // The discounted month only appears when a live discount backs it.
    if (promo?.valid) {
        steps.push({
            tag: t('pw.pay.step2.tag'),
            title: t('pw.pay.step2.title'),
            sub: t('pw.pay.step2.sub', { n: promo.percent }),
            was: money(PRICE_MONTH),
            price: money(promo.price),
            day: perDay(promo.price),
        });
    }

    steps.push({
        tag: t('pw.pay.step3.tag'),
        title: t('pw.pay.step3.title'),
        sub: t('pw.pay.step3.sub'),
        was: null,
        price: money(PRICE_MONTH),
        day: perDay(PRICE_MONTH),
    });

    return steps;
}

const PriceStep: React.FC<{ step: Step; wide?: boolean }> = ({ step, wide }) => {
    const { t } = useTt();

    return (
        <Row
            style={{
                gap: wide ? 20 : 14,
                alignItems: 'center',
                padding: wide ? '18px 22px' : '15px 16px',
                borderRadius: 16,
                border: step.main ? `1px solid ${OK}44` : hair,
                background: step.main ? `${OK}0a` : c.cardIn,
            }}
        >
            {wide && (
                <Meta
                    style={{
                        fontSize: 9.5,
                        letterSpacing: '0.12em',
                        color: step.main ? OK : c.muted,
                        width: 112,
                        flex: '0 0 auto',
                    }}
                >
                    {step.tag}
                </Meta>
            )}
            <Col style={{ flex: 1, minWidth: 0 }} gap={wide ? 4 : 5}>
                {!wide && (
                    <Meta style={{ fontSize: 9, color: step.main ? OK : c.muted, letterSpacing: '0.14em' }}>
                        {step.tag}
                    </Meta>
                )}
                <span
                    style={{ fontSize: wide ? 15.5 : 16, color: c.textPri, fontWeight: 400, letterSpacing: '-0.018em' }}
                >
                    {step.title}
                </span>
                <Muted style={{ fontSize: wide ? 12.5 : 11.5 }}>{step.sub}</Muted>
            </Col>
            <Col style={{ alignItems: 'flex-end', flex: '0 0 auto' }} gap={6}>
                <Row style={{ gap: 7, alignItems: 'baseline' }}>
                    {step.was && (
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 13,
                                color: c.subtle,
                                letterSpacing: '-0.02em',
                                textDecoration: 'line-through',
                            }}
                        >
                            {step.was}
                        </span>
                    )}
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: wide ? 23 : 22,
                            fontWeight: 300,
                            color: step.main ? OK : c.textPri,
                            letterSpacing: '-0.04em',
                        }}
                    >
                        {step.price}
                    </span>
                </Row>
                <Row
                    style={{
                        gap: 6,
                        alignItems: 'baseline',
                        padding: '4px 9px',
                        borderRadius: 8,
                        background: step.main ? `${OK}14` : 'rgba(232,230,224,0.05)',
                    }}
                >
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 12.5,
                            fontWeight: 400,
                            color: step.main ? OK : c.text,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {step.day}
                    </span>
                    <Meta style={{ fontSize: 8.5, color: c.muted, letterSpacing: '0.1em' }}>{t('pw.pay.perDay')}</Meta>
                </Row>
            </Col>
        </Row>
    );
};

/* ── guarantee & FAQ ──────────────────────────────────────────────────────── */

const Guarantee: React.FC<{ wide?: boolean }> = ({ wide }) => {
    const { t } = useTt();
    const badge = (
        <div
            style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: `1px solid ${OK}55`,
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
                margin: wide ? undefined : '0 auto',
            }}
        >
            <Icon k="check" size={18} color={OK} />
        </div>
    );
    const body = (
        <Col gap={8} style={{ textAlign: wide ? 'left' : 'center' }}>
            <span style={{ fontSize: 17, color: c.textPri, fontWeight: 400, letterSpacing: '-0.018em' }}>
                {t('pw.pay.guarantee.title', { n: REFUND_DAYS })}
            </span>
            <Muted style={{ fontSize: wide ? 13.5 : 12, lineHeight: 1.5 }}>{t('pw.pay.guarantee.body')}</Muted>
            <Row style={{ gap: 7, justifyContent: wide ? 'flex-start' : 'center', marginTop: 3 }}>
                <Icon k="out" size={11} color={c.muted} />
                <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.06em', textTransform: 'none' }}>
                    {t('pw.pay.guarantee.portal')}
                </Meta>
            </Row>
        </Col>
    );

    return (
        <div
            style={{
                border: `1px solid ${OK}33`,
                background: `${OK}0a`,
                borderRadius: 20,
                padding: wide ? '22px 26px' : '19px 18px 17px',
            }}
        >
            {wide ? (
                <Row style={{ gap: 18, alignItems: 'flex-start' }}>
                    {badge}
                    {body}
                </Row>
            ) : (
                <Col gap={12}>
                    {badge}
                    {body}
                </Col>
            )}
        </div>
    );
};

const Faq: React.FC = () => {
    const { t } = useTt();
    const vars = { trial: TRIAL_DAYS, price: money(PRICE_MONTH), refund: REFUND_DAYS };

    return (
        <Col gap={0}>
            {[1, 2, 3, 4, 5].map((n, i) => (
                <Acc key={n} title={t(`pw.pay.faq.${n}.q`, vars)} open={i === 0} last={n === 5}>
                    <Muted style={{ fontSize: 13, lineHeight: 1.55 }}>{t(`pw.pay.faq.${n}.a`, vars)}</Muted>
                </Acc>
            ))}
        </Col>
    );
};

const Footer: React.FC = () => {
    const { t } = useTt();
    const links: Array<[string, string]> = [
        ['/terms', t('pw.pay.footer.terms')],
        ['/privacy', t('pw.pay.footer.privacy')],
        ['/refund', t('pw.pay.footer.refunds')],
        ['/contact', t('pw.pay.footer.support')],
    ];

    return (
        <div style={{ padding: '24px 20px 34px', borderTop: hair, marginTop: 26 }}>
            <Row style={{ justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                {links.map(([to, label]) => (
                    <Link key={to} to={to} style={{ textDecoration: 'none' }}>
                        <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.08em' }}>{label}</Meta>
                    </Link>
                ))}
            </Row>
            <Row style={{ justifyContent: 'center', marginTop: 13 }}>
                <Meta style={{ fontSize: 9, color: c.subtle, letterSpacing: '0.12em' }}>
                    {t('pw.pay.footer.rights', { year: new Date().getFullYear() })}
                </Meta>
            </Row>
        </div>
    );
};

/* ── the screen ───────────────────────────────────────────────────────────── */

export interface PaywallProps {
    profile: PaywallProfile;
    /** Accepted promo, or null when none was granted or none is live. */
    promo: PromoState | null;
    /** Time left on the promo, `mm:ss`. Null hides the countdown entirely. */
    timer: string | null;
    counts: { patterns: number; exercises: number };
    onCheckout: () => void;
    busy?: boolean;
    error?: string | null;
}

const Paywall: React.FC<PaywallProps> = ({ profile, promo, timer, counts, onCheckout, busy, error }) => {
    const { t } = useTt();
    const wide = useWide();
    const [view, setView] = React.useState<'mobile' | 'desktop'>('mobile');
    const reviewList = React.useMemo(() => reviews(t), [t]);
    const pairs = React.useMemo(() => shotPairs(t), [t]);
    const steps = React.useMemo(() => priceSteps(t, promo), [t, promo]);

    const ctaNote = t('pw.pay.ctaNote', { n: TRIAL_DAYS });
    const cta = (width?: number | string) => (
        <Cta
            label={t('pw.pay.cta')}
            note={ctaNote}
            onClick={onCheckout}
            disabled={busy}
            width={width}
            height={wide ? 54 : 50}
        />
    );

    const promoBar = promo?.valid && (
        <Row
            style={{
                gap: 11,
                padding: '11px 13px',
                borderRadius: 14,
                border: `1px solid ${OK}33`,
                background: `${OK}0d`,
                alignItems: 'center',
            }}
        >
            <Icon k="check" size={14} color={OK} />
            <span style={{ fontSize: 12.5, color: c.textPri, fontWeight: 400, flex: 1 }}>
                {t('pw.pay.promoApplied')}
            </span>
            {timer && (
                <Row
                    style={{
                        gap: 8,
                        alignItems: 'center',
                        padding: '5px 9px',
                        borderRadius: 9,
                        background: 'rgba(10,10,10,0.5)',
                    }}
                >
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 13,
                            color: ACT,
                            letterSpacing: '-0.02em',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {timer}
                    </span>
                </Row>
            )}
        </Row>
    );

    const errorLine = error && (
        <Row style={{ justifyContent: 'center', marginTop: 10 }}>
            <Meta style={{ fontSize: 10, color: '#f87171', letterSpacing: '0.06em', textTransform: 'none' }}>
                {error}
            </Meta>
        </Row>
    );

    const hero = (
        <Col style={{ textAlign: 'center', maxWidth: wide ? 760 : undefined, margin: '0 auto' }} gap={wide ? 13 : 11}>
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: ACT, letterSpacing: '0.18em' }}>
                {t('pw.pay.eyebrow')}
            </Meta>
            <Serif size={wide ? 46 : 26} style={{ textAlign: 'center', lineHeight: wide ? 1.1 : 1.2 }}>
                {t('pw.pay.title')}
            </Serif>
            <Muted style={{ fontSize: wide ? 16 : 13, lineHeight: 1.5 }}>{t('pw.pay.sub')}</Muted>
        </Col>
    );

    const shotsCarousel = wide ? (
        <Col style={{ alignItems: 'center' }} gap={20}>
            <ViewSwitch value={view} onChange={setView} />
            {/* One carousel of three screens, each slide showing that screen on
                both a phone and a laptop at once. The switch dims the side it is
                not on rather than swapping the content: seeing the same map in
                both places IS the claim, and hiding half of it to prove it is
                self-defeating. Held to the pair's own width so the arrows sit
                beside the shots instead of adrift in empty page. */}
            <div style={{ width: '100%', maxWidth: 920 }}>
                <Carousel pad={0} gap={34} arrows auto={SHOT_MS}>
                    {pairs.map((pair) => (
                        <Row
                            key={pair.phone.src}
                            style={{ gap: 34, width: '100%', justifyContent: 'center', alignItems: 'center' }}
                        >
                            <div
                                style={{
                                    flex: '0 0 auto',
                                    opacity: view === 'mobile' ? 1 : DIMMED,
                                    transition: 'opacity 320ms',
                                }}
                            >
                                <PhoneShot shot={pair.phone} width={268} />
                            </div>
                            <div
                                style={{
                                    flex: '0 0 auto',
                                    opacity: view === 'desktop' ? 1 : DIMMED,
                                    transition: 'opacity 320ms',
                                }}
                            >
                                <LaptopShot shot={pair.laptop} width={600} />
                            </div>
                        </Row>
                    ))}
                </Carousel>
            </div>
        </Col>
    ) : (
        <Carousel card={268} auto={SHOT_MS}>
            {phoneShots(t).map((s) => (
                <PhoneShot key={s.src} shot={s} width={268} />
            ))}
        </Carousel>
    );

    if (wide) {
        return (
            <div style={{ background: c.bg, color: c.text, fontFamily: f.ui, minHeight: '100vh' }}>
                <Row
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 20,
                        height: 66,
                        padding: '0 40px',
                        borderBottom: hair,
                        background: 'rgba(10,10,10,0.94)',
                        backdropFilter: 'blur(18px)',
                        gap: 16,
                    }}
                >
                    <Meta style={{ fontSize: 11, color: c.textPri, letterSpacing: '0.2em', flex: '0 0 auto' }}>
                        DoYourStep
                    </Meta>
                    <Row style={{ gap: 16, marginLeft: 'auto', flex: '0 0 auto', alignItems: 'center' }}>
                        {timer && <Countdown label={t('pw.pay.offerEnds')} time={timer} align="right" size={19} />}
                        <button
                            type="button"
                            onClick={onCheckout}
                            disabled={busy}
                            style={{
                                width: 210,
                                height: 42,
                                borderRadius: 99,
                                border: 'none',
                                background: c.textPri,
                                color: '#0a0a0a',
                                fontFamily: f.ui,
                                fontSize: 14,
                                fontWeight: 500,
                                cursor: busy ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 9,
                            }}
                        >
                            {t('pw.pay.cta')}
                            <Icon k="arrow" size={15} color="#0a0a0a" />
                        </button>
                    </Row>
                </Row>

                <Screen wide style={{ padding: '38px 40px 0', minHeight: 0 }}>
                    <Col gap={72}>
                        <Appear>
                            <Col gap={28}>
                                {hero}
                                <div style={{ maxWidth: 740, margin: '0 auto', width: '100%' }}>
                                    <BeforeAfter profile={profile} wide />
                                </div>
                            </Col>
                        </Appear>

                        <Row
                            style={{
                                gap: 28,
                                alignItems: 'center',
                                maxWidth: 740,
                                margin: '0 auto',
                                width: '100%',
                                padding: '26px 30px',
                                borderRadius: 22,
                                border: `1px solid ${OK}2e`,
                                background: `linear-gradient(150deg, ${OK}0d, ${c.card} 58%)`,
                            }}
                        >
                            <Col style={{ flex: 1, minWidth: 0 }} gap={12}>
                                <Serif size={27} style={{ lineHeight: 1.18 }}>
                                    {t('pw.pay.close.title')}
                                </Serif>
                                <Muted style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                                    {t('pw.pay.close.sub', {
                                        trial: TRIAL_DAYS,
                                        price: money(PRICE_MONTH),
                                        refund: REFUND_DAYS,
                                    })}
                                </Muted>
                                {promoBar}
                                {errorLine}
                            </Col>
                            <div style={{ flex: '0 0 auto' }}>{cta(228)}</div>
                        </Row>

                        <Col gap={30}>
                            <SectHead title={t('pw.pay.shots.title')} sub={t('pw.pay.shots.sub')} wide />
                            {shotsCarousel}
                        </Col>

                        <Col gap={28}>
                            <SectHead title={t('pw.pay.inside.title')} sub={t('pw.pay.inside.sub')} wide />
                            <Inside counts={counts} wide />
                        </Col>

                        {reviewList.length > 0 && (
                            <Col gap={26}>
                                <SectHead title={t('pw.pay.reviews.title')} sub={t('pw.pay.reviews.sub')} wide />
                                <div style={{ maxWidth: 720, margin: '0 auto', width: '100%' }}>
                                    <Carousel pad={0} gap={16} arrows auto={REVIEW_MS}>
                                        {reviewList.map((r) => (
                                            <ReviewCard key={r.name + r.title} review={r} wide />
                                        ))}
                                    </Carousel>
                                </div>
                            </Col>
                        )}

                        <Col gap={26}>
                            <SectHead
                                title={t('pw.pay.trial.title', { n: TRIAL_DAYS })}
                                sub={t('pw.pay.trial.sub')}
                                wide
                            />
                            <Col style={{ maxWidth: 760, margin: '0 auto', width: '100%' }} gap={11}>
                                {steps.map((s) => (
                                    <PriceStep key={s.tag} step={s} wide />
                                ))}
                                <div style={{ marginTop: 4 }}>
                                    <Assurances
                                        center
                                        items={[
                                            t('pw.pay.assure.1'),
                                            t('pw.pay.assure.2'),
                                            t('pw.pay.assure.3', { n: REFUND_DAYS }),
                                        ]}
                                    />
                                </div>
                            </Col>
                        </Col>

                        <Col style={{ alignItems: 'center' }} gap={16}>
                            <SectHead title={t('pw.pay.close.title')} wide />
                            {cta(320)}
                            <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.1em' }}>
                                {t('pw.pay.close.note', { n: REFUND_DAYS })}
                            </Meta>
                        </Col>

                        <div style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
                            <Guarantee wide />
                        </div>

                        <Col gap={24}>
                            <SectHead title={t('pw.pay.faq.title')} wide />
                            <Col style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
                                <Faq />
                            </Col>
                        </Col>

                    </Col>
                    <Footer />
                </Screen>
            </div>
        );
    }

    return (
        <Screen>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 40,
                    background: 'rgba(10,10,10,0.95)',
                    backdropFilter: 'blur(20px)',
                    borderBottom: hair,
                    padding: '9px 14px',
                }}
            >
                <Row style={{ gap: 12 }}>
                    {timer ? (
                        <Countdown label={t('pw.pay.offerEnds')} time={timer} />
                    ) : (
                        <Meta style={{ fontSize: 11, color: c.textPri, letterSpacing: '0.18em', flex: '0 0 auto' }}>
                            DoYourStep
                        </Meta>
                    )}
                    <button
                        type="button"
                        onClick={onCheckout}
                        disabled={busy}
                        style={{
                            flex: 1,
                            height: 42,
                            borderRadius: 99,
                            border: 'none',
                            background: c.textPri,
                            color: '#0a0a0a',
                            fontFamily: f.ui,
                            fontSize: 13.5,
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            cursor: busy ? 'default' : 'pointer',
                        }}
                    >
                        {t('pw.pay.cta')}
                    </button>
                </Row>
            </div>

            <Appear>
                <div style={{ padding: '18px 20px 0' }}>{hero}</div>
                <div style={{ padding: '20px 20px 0' }}>
                    <BeforeAfter profile={profile} />
                </div>
            </Appear>

            <div style={{ padding: '26px 20px 0' }}>
                {promoBar}
                <div style={{ height: promo?.valid ? 14 : 0 }} />
                {cta()}
                {errorLine}
            </div>

            <div style={{ paddingTop: 30 }}>
                <div style={{ padding: '0 20px 14px' }}>
                    <SectHead title={t('pw.pay.shots.title')} sub={t('pw.pay.shots.sub')} />
                </div>
                {shotsCarousel}
            </div>

            <div style={{ paddingTop: 30 }}>
                <div style={{ padding: '0 20px 14px' }}>
                    <SectHead title={t('pw.pay.inside.title')} sub={t('pw.pay.inside.sub')} />
                </div>
                <div style={{ padding: '0 20px' }}>
                    <Inside counts={counts} />
                </div>
            </div>

            {reviewList.length > 0 && (
                <div style={{ paddingTop: 30 }}>
                    <div style={{ padding: '0 20px 14px' }}>
                        <SectHead title={t('pw.pay.reviews.title')} />
                    </div>
                    <Carousel card={290} auto={REVIEW_MS}>
                        {reviewList.map((r) => (
                            <ReviewCard key={r.name + r.title} review={r} clamp={5} />
                        ))}
                    </Carousel>
                </div>
            )}

            <div style={{ paddingTop: 30 }}>
                <div style={{ padding: '0 20px 14px' }}>
                    <SectHead title={t('pw.pay.trial.title', { n: TRIAL_DAYS })} sub={t('pw.pay.trial.sub')} />
                </div>
                <Col style={{ padding: '0 20px' }} gap={9}>
                    {steps.map((s) => (
                        <PriceStep key={s.tag} step={s} />
                    ))}
                    <div style={{ marginTop: 2 }}>
                        <Assurances
                            items={[
                                t('pw.pay.assure.1'),
                                t('pw.pay.assure.2'),
                                t('pw.pay.assure.3', { n: REFUND_DAYS }),
                            ]}
                        />
                    </div>
                </Col>
            </div>

            <div style={{ paddingTop: 30 }}>
                <div style={{ padding: '0 20px 14px' }}>
                    <SectHead title={t('pw.pay.close.title')} />
                </div>
                <div style={{ padding: '0 20px' }}>
                    {cta()}
                    <Row style={{ justifyContent: 'center', marginTop: 13 }}>
                        <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.1em', textAlign: 'center' }}>
                            {t('pw.pay.close.note', { n: REFUND_DAYS })}
                        </Meta>
                    </Row>
                </div>
            </div>

            <div style={{ padding: '26px 20px 0' }}>
                <Guarantee />
            </div>

            <div style={{ paddingTop: 30 }}>
                <div style={{ padding: '0 20px 6px' }}>
                    <SectHead title={t('pw.pay.faq.title')} />
                </div>
                <div style={{ padding: '0 20px' }}>
                    <Faq />
                </div>
            </div>

            <Footer />
        </Screen>
    );
};

export default Paywall;
